import React, { useState, useRef, useCallback, useEffect } from 'react';
// import "../../../styles/FileUploader.css";

import { GoTrash, GoCheckCircle } from "react-icons/go";
import { FiUploadCloud } from "react-icons/fi";
import { AiOutlineFilePdf, AiOutlineFileExcel, AiOutlineFileWord, AiOutlineFileZip, AiOutlineFileImage, AiOutlineFile } from "react-icons/ai";

// import { UploadIcon, TrashIcon, CheckIcon } from "../quote_portal/components/other/Icons";

export function fileUploaderKey(file) {
  return `${file.name}-${file.size}-${file.lastModified}`;
}

const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const getFileIcon = (type) => {
  if (type.startsWith("image/")) return <AiOutlineFileImage />;
  if (type === "application/pdf") return <AiOutlineFilePdf />;
  if (type.includes("spreadsheet") || type.includes("excel")) return <AiOutlineFileExcel />;
  if (type.includes("word") || type.includes("document")) return <AiOutlineFileWord />;
  if (type.includes("zip") || type.includes("compressed")) return <AiOutlineFileZip />;
  return <AiOutlineFile />;
};

const DEFAULT_ALLOWED_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/gif",
];

export default function FileUploader({
  onFileSelect,
  onFilesAdd,
  accept = "application/pdf,image/jpeg,image/jpg,image/png,image/webp,image/gif",
  maxSizeMB = 10,
  multiple = false,
  maxFiles = 10,
  /** When false, parent drives progress via uploadProgressByKey (e.g. real server upload). */
  autoSimulateUpload = true,
  /** Progress 0–100 keyed by fileUploaderKey(file). */
  uploadProgressByKey = null,
  /** MIME type prefixes allowed when set (e.g. image/, video/). Overrides DEFAULT_ALLOWED_TYPES. */
  allowedMimePrefixes = null,
  externalError = "",
  hint = null,
}) {
  const handleSelect = (value) => {
    onFileSelect?.(value ?? null);
  };
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState([]);
  const [error, setError] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState("idle"); // idle | uploading | done
  const inputRef = useRef(null);

  const validateFile = (f) => {
    if (!f) return { ok: false, error: "" };

    if (allowedMimePrefixes?.length) {
      const okType = allowedMimePrefixes.some((prefix) => f.type.startsWith(prefix));
      if (!okType) {
        return {
          ok: false,
          error: "This file type is not allowed.",
        };
      }
    } else if (!DEFAULT_ALLOWED_TYPES.includes(f.type)) {
      return { ok: false, error: "Only PDF and image files (JPG, PNG, WebP, GIF) are allowed." };
    }

    const maxBytes = maxSizeMB * 1024 * 1024;
    if (f.size > maxBytes) {
      return { ok: false, error: `File is too large. Maximum size is ${maxSizeMB}MB.` };
    }
    return { ok: true, error: "" };
  };

  const simulateUpload = () => {
    setUploadStatus("uploading");
    setUploadProgress(0);
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 18 + 5;
      if (progress >= 100) {
        clearInterval(interval);
        setUploadProgress(100);
        setUploadStatus("done");
      } else {
        setUploadProgress(Math.round(progress));
      }
    }, 120);
  };

  const validateAndSetFiles = (selectedFiles) => {
    setError("");
    const incoming = Array.from(selectedFiles || []).filter(Boolean);
    if (incoming.length === 0) return;

    const nextIncoming = incoming.slice(0, Math.max(0, maxFiles));

    for (const f of nextIncoming) {
      const { ok, error: errMsg } = validateFile(f);
      if (!ok) {
        setError(errMsg);
        return;
      }
    }

    if (onFilesAdd) {
      onFilesAdd(nextIncoming);
      if (!autoSimulateUpload) {
        setFiles((prev) => {
          const merged = multiple ? [...prev, ...nextIncoming] : [nextIncoming[0]];
          return merged.slice(0, maxFiles);
        });
        return;
      }
    }

    setFiles((prev) => {
      const merged = multiple ? [...prev, ...nextIncoming] : [nextIncoming[0]];
      const capped = merged.slice(0, maxFiles);
      setUploadStatus("idle");
      setUploadProgress(0);
      handleSelect(multiple ? capped : capped[0] ?? null);
      if (autoSimulateUpload) {
        simulateUpload();
      }
      return capped;
    });
    if (autoSimulateUpload) {
      setUploadStatus("idle");
      setUploadProgress(0);
    }
  };

  // Drop pending rows once parent clears them from uploadProgressByKey (upload finished).
  useEffect(() => {
    if (autoSimulateUpload || !uploadProgressByKey) return;
    setFiles((prev) =>
      prev.filter((f) => uploadProgressByKey[fileUploaderKey(f)] !== undefined),
    );
  }, [uploadProgressByKey, autoSimulateUpload]);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragging(false);
      validateAndSetFiles(e.dataTransfer.files);
    },
    [maxFiles, maxSizeMB, multiple, onFilesAdd, autoSimulateUpload],
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleInputChange = (e) => {
    validateAndSetFiles(e.target.files);
    e.target.value = "";
  };

  const handleRemoveAll = () => {
    setFiles([]);
    setUploadStatus("idle");
    setUploadProgress(0);
    setError("");
    handleSelect(null);
  };

  const handleRemoveOne = (idx) => {
    setFiles((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      handleSelect(multiple ? next : next[0] ?? null);
      return next;
    });
  };

  const displayError = externalError || error;

  const dropZoneClass = [
    "fu-drop-zone",
    isDragging && "fu-drop-zone--dragging",
    displayError && "fu-drop-zone--error",
    (!multiple && files.length > 0) && "fu-drop-zone--disabled",
  ]
    .filter(Boolean)
    .join(" ");

  const getFileProgress = (file) => {
    if (uploadProgressByKey) {
      const key = fileUploaderKey(file);
      const pct = uploadProgressByKey[key];
      if (pct === undefined) return null;
      return pct;
    }
    return uploadProgress;
  };

  const getFileStatus = (file) => {
    if (uploadProgressByKey) {
      const pct = getFileProgress(file);
      if (pct === null) return "idle";
      return pct >= 100 ? "done" : "uploading";
    }
    return uploadStatus;
  };

  return (
    <div className="fu-wrapper">
      <div
        className={dropZoneClass}
        onClick={() => {
          if (!multiple && files.length > 0) return;
          inputRef.current.click();
        }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleInputChange}
          className="fu-hidden-input"
        />

        <div className="fu-upload-icon-wrap">
          <div className={`fu-upload-icon-bg${isDragging ? " fu-upload-icon-bg--active" : ""}`}>
            <FiUploadCloud />
          </div>
        </div>

        <div className="fu-drop-text">
          <span className="fu-click-text">Click to upload</span>
          <span className="fu-or-text"> or drag and drop</span>
        </div>
        <p className="fu-hint">
          {hint ??
            `PDF or image (JPG, PNG, WebP) up to ${maxSizeMB}MB${multiple ? ` (up to ${maxFiles} files)` : ""}`}
        </p>
      </div>

      {displayError && <p className="fu-error-text">{displayError}</p>}

      {files.length > 0 && (
        <>
          {files.map((file, idx) => {
            const progress = getFileProgress(file);
            const status = getFileStatus(file);
            const showProgress = status !== "done" && progress !== null;

            return (
              <div className="fu-file-card" key={`${file.name}-${file.size}-${idx}`}>
                <div className="fu-file-icon-wrap">
                  <span className="fu-file-emoji">{getFileIcon(file.type)}</span>
                </div>

                <div className="fu-file-info">
                  <div className="fu-file-name-row">
                    <span className="fu-file-name" title={file.name}>
                      {file.name.length > 36 ? file.name.slice(0, 33) + "..." : file.name}
                    </span>
                    {status === "done" && (
                      <span className="fu-check-badge">
                        <GoCheckCircle />
                      </span>
                    )}
                  </div>

                  {showProgress ? (
                    <>
                      <span className="fu-file-size">{formatFileSize(file.size)}</span>
                      <div className="fu-progress-track">
                        <div
                          className="fu-progress-bar"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <span className="fu-progress-label">{progress}%</span>
                    </>
                  ) : (
                    <span className="fu-file-size">{formatFileSize(file.size)}</span>
                  )}
                </div>

                <button
                  className="fu-remove-btn"
                  onClick={() => (multiple ? handleRemoveOne(idx) : handleRemoveAll())}
                  title="Remove file"
                  type="button"
                >
                  <GoTrash />
                </button>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
